import React, { useState, useEffect } from 'react';
import Web3 from 'web3';
import { networks } from '../config/settings';
import tokenABI from '../abi/ADBYTES.json';
import bridgeContractABI from '../abi/ADBYTESBridge.json';
import Toast from './Toast'; // Import the Toast component
import { CopyToClipboard } from 'react-copy-to-clipboard'; // Import CopyToClipboard

const POLLING_INTERVAL = 10000; // Polling interval in milliseconds (e.g., 10000ms = 10s)

const TokenBridge = () => {
  const [currentNetwork, setCurrentNetwork] = useState('');
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [amount, setAmount] = useState('');
  const [erc20Balance, setErc20Balance] = useState('0');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [validationError, setValidationError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [approvalNeeded, setApprovalNeeded] = useState(true);
  const [bridgeContractAddress, setBridgeContractAddress] = useState('');
  const [bridgeEntries, setBridgeEntries] = useState([]);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    const web3 = window.ethereum ? new Web3(window.ethereum) : null;
    let pollingInterval;

    const loadNetworkData = async () => {
      if (!web3) return;

      const networkId = await web3.eth.net.getId();
      const hexNetworkId = `0x${networkId.toString(16)}`;
      setCurrentNetwork(hexNetworkId);

      const accounts = await web3.eth.getAccounts();
      if (accounts.length > 0) {
        setDestinationAddress(accounts[0]);

        const network = Object.values(networks).find(net => net.chainId === hexNetworkId);
        if (network) {
          const tokenAddress = network.contractAddress;
          const tokenContract = new web3.eth.Contract(tokenABI, tokenAddress);

          const balance = await tokenContract.methods.balanceOf(accounts[0]).call();
          setErc20Balance(web3.utils.fromWei(balance, 'ether'));

          const otherNetworks = Object.values(networks).filter(net => net.chainId !== hexNetworkId);
          setAvailableNetworks(otherNetworks);
          setSelectedNetwork(otherNetworks[0]?.chainId || '');

          setBridgeContractAddress(network.bridgeContractAddress);

          // Check allowance for the bridge contract
          const allowance = await tokenContract.methods.allowance(accounts[0], network.bridgeContractAddress).call();
          if (parseFloat(web3.utils.fromWei(allowance, 'ether')) >= parseFloat(amount)) {
            setApprovalNeeded(false);
          } else {
            setApprovalNeeded(true);
          }

          // Fetch bridge entries
          const bridgeContract = new web3.eth.Contract(bridgeContractABI, network.bridgeContractAddress);
          const entries = await bridgeContract.methods.getBridgeEntriesByAddress(accounts[0]).call();
          setBridgeEntries(entries.reverse()); // Reverse the array to show the latest first
        } else {
          console.error("Unsupported network");
        }
      }
    };

    if (web3) {
      loadNetworkData();
      window.ethereum.on('accountsChanged', loadNetworkData);
      window.ethereum.on('chainChanged', loadNetworkData);

      // Set up polling interval to refresh the balance
      pollingInterval = setInterval(loadNetworkData, POLLING_INTERVAL);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', loadNetworkData);
        window.ethereum.removeListener('chainChanged', loadNetworkData);
      }
      clearInterval(pollingInterval); // Clear the polling interval on component unmount
    };
  }, [amount]);

  const handleChangeAmount = (e) => {
    const value = e.target.value;
    setAmount(value);

    if (isNaN(value) || value <= 0) {
      setValidationError('Amount must be greater than 0');
    } else if (parseFloat(value) > parseFloat(erc20Balance)) {
      setValidationError('Amount exceeds current balance');
    } else {
      setValidationError('');
    }
  };

  const handleChangeDestination = (e) => {
    const value = e.target.value;
    setDestinationAddress(value);

    if (!Web3.utils.isAddress(value)) {
      setValidationError('Invalid wallet address');
    } else {
      setValidationError('');
    }
  };

  const handleApprove = async () => {
    try {
      const web3 = new Web3(window.ethereum);
      const accounts = await web3.eth.getAccounts();
      const network = Object.values(networks).find(net => net.chainId === currentNetwork);
      const tokenContract = new web3.eth.Contract(tokenABI, network.contractAddress);

      setModalMessage('Waiting for transaction confirmation...');
      setModalVisible(true);

      // Fetch the current gas price
      const gasPrice = await web3.eth.getGasPrice();

      await tokenContract.methods.approve(network.bridgeContractAddress, web3.utils.toWei(amount, 'ether')).send({ from: accounts[0], gasPrice })
        .on('transactionHash', (hash) => {
          setModalMessage(`Transaction sent! Waiting for confirmations... Tx Hash: ${hash}`);
        })
        .on('receipt', (receipt) => {
          setModalMessage('Transaction confirmed!');
          setApprovalNeeded(false);
        })
        .on('error', (error) => {
          setModalMessage(`Transaction failed: ${error.message}`);
        });
    } catch (error) {
      setModalMessage(`Error: ${error.message}`);
    }
  };

  const handleBridgeTokens = async () => {
    try {
      const web3 = new Web3(window.ethereum);
      const accounts = await web3.eth.getAccounts();
      const network = Object.values(networks).find(net => net.chainId === currentNetwork);

      // Ensure the bridge contract ABI is loaded
      if (!bridgeContractABI || !bridgeContractAddress) {
        setModalMessage('Bridge contract not loaded.');
        setModalVisible(true);
        return;
      }

      const bridgeContract = new web3.eth.Contract(bridgeContractABI, bridgeContractAddress);
      const destinationChainId = Web3.utils.hexToNumber(selectedNetwork);

      setModalMessage('Waiting for transaction confirmation...');
      setModalVisible(true);

      // Fetch the current gas price
      const gasPrice = await web3.eth.getGasPrice();

      await bridgeContract.methods.enterBridge(web3.utils.toWei(amount, 'ether'), destinationAddress, destinationChainId).send({ from: accounts[0], gasPrice })
        .on('transactionHash', (hash) => {
          setModalMessage(`Transaction sent! Waiting for confirmations... Tx Hash: ${hash}`);
        })
        .on('receipt', (receipt) => {
          setModalMessage('Tokens bridged successfully!');
          setAmount(''); // Reset the amount field
        })
        .on('error', (error) => {
          setModalMessage(`Transaction failed: ${error.message}`);
        });
    } catch (error) {
      setModalMessage(`Error: ${error.message}`);
    }
  };

  const formatChainName = (chainId) => {
    const network = Object.values(networks).find(net => net.chainId === `0x${chainId.toString(16)}`);
    return network ? network.chainName : chainId;
  };

  const handleCopy = () => {
    setToastMessage('Copied to Clipboard');
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2000); // Hide the toast after 2 seconds
  };

  const truncateText = (text, frontChars = 6, backChars = 4) => {
    if (text.length <= frontChars + backChars) return text;
    return `${text.substring(0, frontChars)}...${text.substring(text.length - backChars)}`;
  };

  const network = Object.values(networks).find(net => net.chainId === currentNetwork);

  return (
    <div className="token-bridge token-card bg-white shadow-lg rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Token Bridge</h3>
      <p className="text-md mb-4">
        Use this tool to transfer ADBYTES tokens onto another supported blockchain. Tokens will be burned on this chain and
        subsequently minted on the destination chain. Bridge exits are processed automatically and cost zero fees.
      </p>
      <form>
        <p className="mb-2">
          <strong>Current Network:</strong> {network ? network.chainName : 'Unsupported Network'}
        </p>
        {network && (
          <p className="mb-4 text-sm">
            <strong>Bridge Contract:</strong>{' '}
            <CopyToClipboard text={bridgeContractAddress} onCopy={handleCopy}>
              <span className="text-blue-500 cursor-pointer">{bridgeContractAddress}</span>
            </CopyToClipboard>
          </p>
        )}
        <div className="mb-4">
          <label htmlFor="network-select" className="block mb-2">Bridge to Network:</label>
          <select
            id="network-select"
            className="block w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedNetwork}
            onChange={(e) => setSelectedNetwork(e.target.value)}
          >
            {availableNetworks.map((network) => (
              <option key={network.chainId} value={network.chainId}>
                {network.chainName}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-4">
          <label htmlFor="amount" className="block mb-2">Amount:</label>
          <input
            id="amount"
            type="number"
            className="block w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={amount}
            onChange={handleChangeAmount}
          />
          <small className="text-gray-500">Current Balance: {new Intl.NumberFormat().format(erc20Balance)} ADBYTES</small>
        </div>
        <div className="mb-4">
          <label htmlFor="destination" className="block mb-2">Destination Address:</label>
          <input
            id="destination"
            type="text"
            className="block w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={destinationAddress}
            onChange={handleChangeDestination}
          />
          <small className="text-gray-500">Make sure to double check the full address.</small>
        </div>
        {validationError && <p className="text-red-500">{validationError}</p>}
        <button
          type="button"
          className={`w-full py-2 rounded text-white ${!amount || parseFloat(amount) <= 0 || validationError ? 'bg-gray-300 cursor-not-allowed' : approvalNeeded ? 'bg-green-500 hover:bg-green-700' : 'bg-blue-500 hover:bg-blue-700'}`}
          disabled={!amount || parseFloat(amount) <= 0 || !!validationError}
          onClick={approvalNeeded ? handleApprove : handleBridgeTokens}
        >
          {approvalNeeded ? 'Approve Supply' : 'Bridge Tokens'}
        </button>
      </form>

      {modalVisible && (
        <div className="modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="modal-content bg-white p-6 rounded shadow-lg">
            <p>{modalMessage}</p>
            <button className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700" onClick={() => setModalVisible(false)}>Ok</button>
          </div>
        </div>
      )}

      {bridgeEntries.length > 0 && (
        <div className="bridge-history mt-6">
          <h3 className="text-xl font-semibold mb-4">Bridge History</h3>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border p-2 bg-gray-100">Reference ID</th>
                <th className="border p-2 bg-gray-100">Amount</th>
                <th className="border p-2 bg-gray-100">Destination Address</th>
                <th className="border p-2 bg-gray-100">Destination Chain</th>
                <th className="border p-2 bg-gray-100">Status</th>
              </tr>
            </thead>
            <tbody>
              {bridgeEntries.map((entry, index) => (
                <tr key={index}>
                  <td className="border p-2 cursor-pointer">
                    <CopyToClipboard text={entry.referenceId} onCopy={handleCopy}>
                      <span>{truncateText(entry.referenceId)}</span>
                    </CopyToClipboard>
                  </td>
                  <td className="border p-2">{new Intl.NumberFormat('en-US', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Web3.utils.fromWei(entry.amount, 'ether'))}</td>
                  <td className="border p-2 cursor-pointer">
                    <CopyToClipboard text={entry.destinationAddress} onCopy={handleCopy}>
                      <span>{truncateText(entry.destinationAddress)}</span>
                    </CopyToClipboard>
                  </td>
                  <td className="border p-2">{formatChainName(entry.destinationChainId)}</td>
                  <td className="border p-2">{entry.completed ? 'Completed' : 'Pending'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Toast message={toastMessage} visible={toastVisible} />
    </div>
  );
};

export default TokenBridge;
